import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Modal } from "@/pages/Dashboard";
import { Loader2, Mail, Clock, Check, X } from "lucide-react";

export default function ShareClassModal({ classId, className, onClose }) {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/classes/${classId}/members`).then((r) => setData(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [classId]);

  const invite = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post(`/classes/${classId}/invite`, { email });
      setData(data);
      setEmail("");
      toast.success("Colleague invited");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const removeMember = async (member_id, email) => {
    const params = member_id ? `member_id=${member_id}` : `email=${encodeURIComponent(email)}`;
    const { data } = await api.delete(`/classes/${classId}/members?${params}`);
    setData(data);
  };

  return (
    <Modal title={`Share "${className || "class"}"`} onClose={onClose}>
      <p className="text-sm text-slate-500 -mt-2 mb-4">
        Add a colleague by email. If they have an account they'll see this class immediately; otherwise they'll get access the moment they sign up.
      </p>

      {data?.is_owner === true && (
        <form onSubmit={invite} className="flex gap-2 mb-5">
          <input data-testid="invite-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@school.ie"
            className="flex-1 bg-white border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-[#81B29A] focus:ring-2 focus:ring-[#81B29A]/40" />
          <button data-testid="send-invite-btn" disabled={saving} className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-xl px-4 font-semibold font-['Outfit'] flex items-center gap-1.5 disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />} Invite
          </button>
        </form>
      )}

      {!data ? (
        <div className="flex justify-center py-6 text-slate-300"><Loader2 className="animate-spin" /></div>
      ) : (
        <div className="space-y-2">
          <Row icon={<Check size={14} className="text-emerald-500" />} name={data.owner?.name} email={data.owner?.email} tag="Owner" />
          {data.members.map((m) => (
            <Row key={m.id} testId={`member-${m.id}`} icon={<Check size={14} className="text-emerald-500" />} name={m.name} email={m.email} tag="Has access"
              onRemove={data.is_owner ? () => removeMember(m.id, "") : null} />
          ))}
          {data.pending_invites.map((e) => (
            <Row key={e} testId={`pending-${e}`} icon={<Clock size={14} className="text-amber-500" />} name={e} email="Pending — not registered yet" tag="Pending"
              onRemove={data.is_owner ? () => removeMember("", e) : null} />
          ))}
        </div>
      )}
    </Modal>
  );
}

function Row({ icon, name, email, tag, onRemove, testId }) {
  return (
    <div data-testid={testId} className="flex items-center gap-3 p-3 rounded-xl border border-slate-100">
      <span className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-800 text-sm truncate font-['Outfit']">{name || "—"}</p>
        <p className="text-xs text-slate-400 truncate">{email}</p>
      </div>
      <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{tag}</span>
      {onRemove && <button onClick={onRemove} className="text-slate-300 hover:text-red-500"><X size={15} /></button>}
    </div>
  );
}
